import { useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { PageShell } from '@/features/public-site/page-shell'
import { SimpleHero, PricingCta } from '@/features/public-site/sections'
import { fetchBillingOptions } from '@/features/billing/services/billing-service'
import type { BillingChannelAvailability, BillingCreditPack, BillingPlan } from '@/features/billing/types'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'

/**
 * 套餐通用能力清单（按 tier 匹配，不显示具体小时数）。
 * 额度拆分（转录/配音/音乐/图片分别计量）为下一步，当前只展示能力项。
 */
type PlanTier = 'free' | 'creator' | 'studio'

const PLAN_FEATURES: Record<PlanTier, string[]> = {
  free: [
    '单文件上传最大 400MB',
    '云端素材空间 100MB',
    '云端作品保存 7 天',
    '本地编辑项目永久保留，不占云端空间',
    '无水印基础剪辑与导出',
    '本地项目与基础素材管理',
    '每月智能切片、转录与字幕额度',
    '高级 AI 创作可按点数使用',
    '使用官方模板与基础字幕样式',
  ],
  creator: [
    '单文件上传最大 400MB',
    '云端素材空间 1GB',
    '云端作品保存 30 天',
    '本地编辑项目永久保留，不占云端空间',
    '无水印导出',
    '更高的智能切片、转录与字幕月度额度',
    'AI 配音、音乐、生图：按高级点数使用',
    'AI 视频、数字人、声音复刻：按高级点数使用',
    '口播视频工作流（按所用 AI 能力计费）',
    '本地 MCP 自动化',
    '云端素材库上传与项目素材管理',
    '使用官方品牌模板',
    '会员专属：模板与样式自定义能力逐步开放',
  ],
  studio: [
    '单文件上传最大 1GB',
    '云端素材空间 1GB',
    '云端作品保存 30 天',
    '本地编辑项目永久保留，不占云端空间',
    '包含 Creator 的全部会员权益',
    '最高智能切片、转录与字幕月度额度',
    '智能切片与批量任务优先处理',
    'AI 配音、音乐、生图：按高级点数使用',
    'AI 视频、数字人、声音复刻：按高级点数使用',
    '口播视频与多版本生产工作流',
    '本地 MCP 自动化与优先处理',
    '云端素材库上传与更完整的项目资产管理',
    '品牌模板：新建、复制、编辑与共享（逐步开放）',
    '上传字体、自定义字幕样式与品牌规范（逐步开放）',
    '批量多版本与更高导出规格',
  ],
}

type CycleFilter = 'monthly' | 'yearly'

function formatPrice(price: string | number): string {
  const num = typeof price === 'string' ? parseFloat(price) : price
  if (isNaN(num) || num === 0) return '¥0'
  return `¥${num}`
}

function formatStorageBytes(bytes: number) {
  return bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(0)}GB` : `${Math.round(bytes / 1024 ** 2)}MB`
}

function planFeaturesWithStorage(features: string[], policy: BillingPlan['storagePolicy']) {
  const rest = features.filter((feature) => !feature.startsWith('单文件上传') && !feature.startsWith('云端素材空间') && !feature.startsWith('云端作品保存') && !feature.startsWith('本地编辑项目'))
  if (!policy) return features
  return [`单文件上传最大 ${formatStorageBytes(policy.maxUploadBytes)}`, `云端素材空间 ${formatStorageBytes(policy.assetStorageBytes)}`, `云端作品保存 ${policy.projectRetentionDays} 天`, '本地编辑项目永久保留，不占云端空间', ...rest]
}

interface DisplayPlan {
  key: string
  name: string
  price: string
  originalPrice: string | null
  period: string
  badge: string | null
  features: string[]
  ctaLabel: string
  highlighted: boolean
}

function buildDisplayPlans(plans: BillingPlan[], cycle: CycleFilter): DisplayPlan[] {
  const result: DisplayPlan[] = []

  // 免费版：取 free 套餐或固定显示
  const freePlan = plans.find((p) => p.plan_code.includes('free'))
  result.push({
    key: 'free',
    name: '免费版',
    price: '¥0',
    originalPrice: null,
    period: '/永久',
    badge: null,
    features: planFeaturesWithStorage(PLAN_FEATURES.free, freePlan?.storagePolicy),
    ctaLabel: '免费开始',
    highlighted: false,
  })

  // Creator
  const creatorPlan = plans.find(
    (p) => p.plan_code.includes('creator') && p.billing_cycle === cycle,
  )
  if (creatorPlan) {
    result.push({
      key: creatorPlan.plan_code,
      name: 'Creator',
      price: formatPrice(creatorPlan.price),
      originalPrice: creatorPlan.original_price
        ? formatPrice(creatorPlan.original_price)
        : null,
      period: cycle === 'yearly' ? '/年' : '/月',
      badge: null,
      features: planFeaturesWithStorage(PLAN_FEATURES.creator, creatorPlan.storagePolicy),
      ctaLabel: '立即开通',
      highlighted: false,
    })
  }

  // Studio（标记热门）
  const studioPlan = plans.find(
    (p) => p.plan_code.includes('studio') && p.billing_cycle === cycle,
  )
  if (studioPlan) {
    result.push({
      key: studioPlan.plan_code,
      name: 'Studio',
      price: formatPrice(studioPlan.price),
      originalPrice: studioPlan.original_price
        ? formatPrice(studioPlan.original_price)
        : null,
      period: cycle === 'yearly' ? '/年' : '/月',
      badge: '热门',
      features: planFeaturesWithStorage(PLAN_FEATURES.studio, studioPlan.storagePolicy),
      ctaLabel: '立即开通',
      highlighted: true,
    })
  }

  return result
}

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
})

function PricingPage() {
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cycle, setCycle] = useState<CycleFilter>('monthly')
  const [purchaseView, setPurchaseView] = useState<'subscription' | 'credits'>('subscription')
  const [creditPacks, setCreditPacks] = useState<BillingCreditPack[]>([])
  const [availability, setAvailability] = useState<BillingChannelAvailability | null>(null)
  const session = useAuthSessionStore((state) => state.session)
  const openPurchase = useMembershipDialogStore((state) => state.openDialog)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchBillingOptions()
      .then((payload) => {
        if (cancelled) return
        setPlans(payload.plans)
        setCreditPacks(payload.creditPacks ?? [])
        setAvailability(payload.availability)
        setError(null)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : '暂时无法加载套餐，请稍后重试。')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const displayPlans = buildDisplayPlans(plans, cycle)
  const hasYearly = plans.some((p) => p.billing_cycle === 'yearly')
  const activeSubscription = Boolean(
    session?.membership?.planCode &&
      ['active', 'trialing', 'grace_period'].includes(session.membership.status ?? ''),
  )

  return (
    <PageShell>
      <SimpleHero
        eyebrow="价格"
        title="选择适合你的"
        highlighted="套餐"
        description="免费开始，准备好再升级。随时可取消。"
        buttonLabel="开始免费试用"
        secondaryLabel="阅读文档"
        secondaryHref="/help"
      />

      {/* Toggle */}
      <div className="mt-10 flex items-center justify-center gap-2">
        <button
          onClick={() => setCycle('monthly')}
          className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
            cycle === 'monthly'
              ? 'bg-accent text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          按月
        </button>
        <button
          onClick={() => setCycle('yearly')}
          disabled={!hasYearly}
          className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
            cycle === 'yearly'
              ? 'bg-accent text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          } ${!hasYearly ? 'cursor-not-allowed opacity-40' : ''}`}
        >
          按年
        </button>
        {hasYearly && (
          <span className="ml-1 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">
            年付更划算
          </span>
        )}
      </div>

      <div className="mx-auto mt-8 flex w-fit rounded-full border border-border bg-card p-1" role="tablist" aria-label="购买类型">
        <button type="button" role="tab" aria-selected={purchaseView === 'subscription'} onClick={() => setPurchaseView('subscription')} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${purchaseView === 'subscription' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>订阅套餐</button>
        <button type="button" role="tab" aria-selected={purchaseView === 'credits'} onClick={() => setPurchaseView('credits')} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${purchaseView === 'credits' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>高级生成点数</button>
      </div>

      {/* Plans */}
      {error ? (
        <div className="mx-auto mt-16 max-w-md px-4 text-center">
          <p className="text-sm text-muted-foreground">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 rounded-full border border-border px-6 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            重新加载
          </button>
        </div>
      ) : (
        purchaseView === 'subscription' ? <><p className="mx-auto mt-6 max-w-3xl px-4 text-center text-sm leading-6 text-muted-foreground">订阅包含基础剪辑与智能切片、转录和字幕的月度额度；AI 配音、音乐、生图、视频、数字人及声音复刻等高级生成能力，按实际使用高级生成点数。</p><p className="mx-auto mt-3 max-w-3xl px-4 text-center text-xs leading-5 text-muted-foreground">本地项目由你的设备保存，不设有效期；只有上传、云端渲染、分享或跨设备同步产生的云端副本适用上述保存期限。</p><div className="mx-auto mt-10 grid max-w-5xl gap-6 px-4 sm:px-6 md:grid-cols-3">
          {displayPlans.map((plan) => {
            const isCurrentPlan = activeSubscription && session?.membership?.planCode === plan.key
            return (
            <div
              key={plan.key}
              className={`relative rounded-2xl border p-6 ${
                isCurrentPlan
                  ? 'border-[#5255f6] bg-[#f7f7ff] shadow-[0_12px_30px_rgba(82,85,246,.12)]'
                  : plan.highlighted
                  ? 'border-primary shadow-lg'
                  : 'border-border bg-card'
              }`}
            >
              {isCurrentPlan ? <span className="absolute right-5 top-5 rounded-full bg-[#5255f6] px-2.5 py-1 text-[11px] font-semibold text-white">当前套餐</span> : null}
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-foreground">{plan.name}</h2>
                {plan.badge && !isCurrentPlan ? (
                  <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
                    {plan.badge}
                  </span>
                ) : null}
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                {plan.originalPrice ? (
                  <span className="text-2xl font-semibold text-muted-foreground line-through">
                    {plan.originalPrice}
                  </span>
                ) : null}
                <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                {plan.period ? (
                  <span className="text-sm text-muted-foreground">{plan.period}</span>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {plan.key === 'free'
                  ? '永久免费，体验核心能力'
                  : cycle === 'yearly'
                    ? '按年计费，到期可续订'
                    : '按月计费，随时可取消'}
              </p>
              {isCurrentPlan ? <div className="mt-6 inline-flex h-10 w-full items-center justify-center rounded-full border border-[#c9cbff] bg-white text-sm font-semibold text-[#4245df]">当前有效订阅</div> : plan.key === 'free' ? <Link
                to={session ? '/app' : '/register'}
                className={`mt-6 inline-flex h-10 w-full items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                  plan.highlighted
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border border-border text-foreground hover:bg-accent'
                }`}
              >
                {loading ? '加载中...' : plan.ctaLabel}
              </Link> : <button
                type="button"
                onClick={() => { if (!session) { window.location.assign('/login?redirect=/pricing'); return }; openPurchase({ preferredPlanCode: plan.key }) }}
                className={`mt-6 inline-flex h-10 w-full items-center justify-center rounded-full text-sm font-semibold transition-colors ${plan.highlighted ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'border border-border text-foreground hover:bg-accent'}`}
              >{loading ? '加载中...' : plan.ctaLabel}</button>}
              <ul className="mt-6 space-y-3">
                {plan.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex gap-2 border-b border-border pb-3 text-sm text-muted-foreground"
                  >
                    <span className="text-primary">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            )
          })}
        </div></> : <section className="mx-auto mt-10 max-w-5xl px-4 sm:px-6"><div className="mx-auto max-w-2xl text-center"><p className="text-xs font-semibold tracking-wider text-primary">按需补充容量</p><h2 className="mt-3 text-3xl font-semibold text-foreground">高级生成点数</h2><p className="mt-3 text-sm leading-6 text-muted-foreground">用于 AI 配音、音乐、生图、AI 视频、数字人和声音复刻等 AI 创作能力。点数余额不随月订阅清零，金额与有效期以后端当前配置为准。</p></div><div className="mx-auto mt-8 grid max-w-3xl gap-5 sm:grid-cols-2">{creditPacks.map((pack) => <article key={pack.packCode} className="border border-border bg-card p-6"><h3 className="text-lg font-semibold text-foreground">{pack.packName}</h3><p className="mt-2 text-sm text-muted-foreground">{pack.quotaUnits} {pack.quotaMetric}</p><p className="mt-5 text-3xl font-bold text-foreground">¥{pack.price}</p><p className="mt-2 text-xs text-muted-foreground">{pack.expiresDays ? `有效期 ${pack.expiresDays} 天` : '长期有效'}</p><button type="button" onClick={() => { if (!session) { window.location.assign('/login?redirect=/pricing'); return }; openPurchase({ preferredCreditPackCode: pack.packCode }) }} className="mt-6 inline-flex h-10 w-full items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90">选择点数包</button></article>)}</div>{creditPacks.length === 0 && !loading ? <p className="mt-10 text-center text-sm text-muted-foreground">当前暂无可购买的高级生成点数包。</p> : null}</section>
      )}

      {availability && <p className="mx-auto mt-8 max-w-5xl px-4 text-center text-xs text-muted-foreground sm:px-6">支付状态：{availability.alipay ? availability.alipayMessage : '支付宝暂不可用'} · {availability.wechat ? (availability.wechatMessage ?? '微信支付可用') : '微信支付暂不可用'}</p>}

      <PricingCta />
    </PageShell>
  )
}
